import type { Immutable } from "immer";
import React, { useEffect, useRef } from "react";
import { getGlobalSpacingClasses } from "tsi-common-react/src/components/_spacing/globalSpacing";
import { useRovingTabList } from "tsi-common-react/src/hooks/useRovingTabList";
import { pushPrebuiltGA4Event } from "tsi-common-react/src/utils/analytics";
import { money } from "tsi-common-react/src/utils/format";
import { usePageSetting } from "tsi-common-react/src/utils/settings";
import { isElementVisible } from "tsi-common-react/src/utils/visibility";
import { t } from "ttag";

import type { FeaturedProductsTabBlock as FeaturedProductsTabBlockValue } from "@reactivated";

import { constants } from "@reactivated/constants";

import { RichTextBlock } from "@thelabnyc/thelabui/src/components/RichText";
import { getWindow } from "@thelabnyc/thelabui/src/utils/dom";
import { richT } from "@thelabnyc/thelabui/src/utils/richText";
import { concatClassNames } from "@thelabnyc/thelabui/src/utils/styles";

const AppSlug = constants["tsicommon.types.AppSlug"];

/**
 * Faithful client port of the legacy server-rendered TP-local
 * ``cms/blocks/featured-products-tab-block.html`` (#33449 / SSR-V2 Phase 1).
 *
 * Tab switching (roving tabindex, click, keyboard, and URL-hash deep-linking)
 * is owned here via the shared ``useRovingTabList`` hook and component state:
 * the active tab carries ``aria-selected="true"`` + ``tabIndex=0`` and its
 * ``[role="tabpanel"]`` drops the ``hidden`` attribute, keyed on the panel /
 * button id pairing. The panel ids (``panel_id``) must equal the matching
 * button's ``aria-controls``; the element ids (``tab_id`` / ``panel_id``) are
 * precomputed server-side (``featured_products_tab.py``: ``striptags`` +
 * ``slugify`` + 1-based loop counter) and used verbatim (also the hash
 * deep-link targets).
 *
 * Everything the legacy template computed server-side is SERIALIZED by the
 * block:
 *
 *   - ``header_content`` / ``tab.tab_label`` / ``tab.tab_content`` /
 *     ``tab.testimonial`` arrive as BARE expanded HTML (the Python
 *     ``_bare_richtext``); the legacy ``{% include_block %}`` wrapped each in
 *     ``<div class="rich-text">`` and the live SCSS targets those wrappers
 *     (``nav > .rich-text``, ``[role="tab"] .rich-text``, and the
 *     ``[role="tabpanel"]`` flex children), so each is re-wrapped here via
 *     ``<RichTextBlock className="rich-text">`` (OffersBlock precedent: exactly
 *     one ``.rich-text`` div). Optional empty fields serialize to ``""`` and
 *     render ``<div class="rich-text"></div>``, matching the legacy
 *     empty-field render.
 *   - ``tab.product_info``: PDP link (``product_pdp_link``), the review
 *     aggregate (product -> parent fallback, gated on ``num_reviews > 0``), the
 *     "Starting at" gate (``not product.parent and product.children.count``),
 *     and the ``purchase_info_for_product`` price (``null`` unless
 *     ``price.exists`` -- its presence is the client equivalent of the legacy
 *     ``{% if purchase_info.price.exists %}`` gate).
 *   - ``value.financing_url``: ``{% slugurl 'finance-your-purchase' %}``.
 *   - ``value.view_item_list_event``: the PRE-BUILT GA4 ``view_item_list``
 *     event (same ``tsicommon_tracker_tags`` builder the legacy
 *     ``{% track_view_item_list %}`` tag used), pushed verbatim on mount and
 *     gated on element visibility (mirroring the legacy
 *     ``push_to_datalayer.html`` ``_isVisible`` walk). Server-side DNT requests
 *     serialize a null event (legacy ``{% jstracker %}`` parity).
 *
 * Star ratings reproduce ``tsicommon_cms/blocks/sub-blocks/star-rating.html``
 * (the variant the legacy block included via the ``class=''`` arg) MARKUP-FOR-
 * MARKUP rather than via the shared ``RatingGraphic`` component: that component
 * omits the ``.star-rating__inner-container`` wrapper and emits the review
 * count as ``.star-rating__review-number`` instead of ``.star-rating__number``,
 * both of which the live partial
 * (``styles/blocks/sub-blocks/_star-rating.scss``) styles as load-bearing
 * layout (the inner-container is the centering flex row; ``__number`` carries
 * the count's type/color) -- so RatingGraphic would visually diverge here. The
 * star SVG paths are the 50x50 ``svg-django/star{,-half-outline,-outline}.svg``
 * sprites (byte-identical to the FeaturedProductsCard ``MoleculeStarRating``
 * constants), with the molecule's full/half thresholds
 * (full at ``rating >= i + 0.75``, half at ``rating >= i + 0.25``). The molecule
 * self-hides on the ``tempurca`` app slug; reproduced via ``usePageSetting``.
 *
 * NOTE: the legacy financing branch keyed on ``product_card.financing_copy``,
 * a variable that is NOT bound in this template's context, so the
 * ``== 'special'`` test was always false and the per-month link branch always
 * rendered. Reproduced here (no "special financing" branch, no ``financing_copy``
 * input on the block).
 */

type Value = FeaturedProductsTabBlockValue;
type ProductTab = NonNullable<Value["product_tabs"]>[number];
type ProductInfo = NonNullable<ProductTab["product_info"]>;
type ProductReview = NonNullable<ProductInfo["review"]>;
type ProductPrice = NonNullable<ProductInfo["price"]>;

// --- Star rating (sub-block molecule parity) ---------------------------------
// The 50x50 ``svg-django/star{,-half-outline,-outline}.svg`` sprite paths the
// legacy ``sub-blocks/star-rating.html`` rendered (byte-identical to the
// FeaturedProductsCard ``MoleculeStarRating`` constants).
const STAR_PATH =
    "M49.2 21.1 38.3 31.7l2.6 15v.6c0 .8-.4 1.5-1.2 1.5-.4 0-.8-.2-1.2-.4l-13.5-7-13.5 7.1c-.4.2-.8.4-1.2.4-.9 0-1.3-.7-1.3-1.5 0-.2 0-.4.1-.6l2.6-15L.8 21.1c-.4-.4-.8-.9-.8-1.5 0-.9.9-1.3 1.7-1.4L16.8 16l6.8-13.7c.3-.6.8-1.2 1.5-1.2s1.2.7 1.5 1.2L33.4 16l15.1 2.2c.7.1 1.7.5 1.7 1.4-.2.6-.6 1.1-1 1.5";
const STAR_HALF_OUTLINE_PATH =
    "m38.3 31.7 2.6 15c.2 1.3-.3 2.1-1.2 2.1-.3 0-.7-.1-1.2-.4l-13.5-7-13.5 7.1c-.5.2-.9.4-1.2.4-.9 0-1.4-.8-1.2-2.1l2.6-15L.8 21.1c-1.3-1.3-.9-2.6.9-2.8l15.1-2.2 6.8-13.7q.6-1.2 1.5-1.2c.5 0 1.1.4 1.5 1.2l6.8 13.7 15.1 2.2c1.8.3 2.2 1.5.9 2.8zm5.1-10.3-10.7-1.6-2-.3-.9-1.8L25 8.1V37l1.8.9 9.6 5.1-1.8-10.7-.4-2 1.5-1.4z";
const STAR_OUTLINE_PATH =
    "M49.2 21.1 38.3 31.7l2.6 15v.6c0 .8-.4 1.5-1.2 1.5-.4 0-.8-.2-1.2-.4l-13.5-7-13.5 7.1c-.4.2-.8.4-1.2.4-.9 0-1.3-.7-1.3-1.5 0-.2 0-.4.1-.6l2.6-15-11-10.7c-.3-.4-.7-.9-.7-1.5 0-.9.9-1.3 1.7-1.4L16.8 16l6.8-13.7c.3-.6.8-1.2 1.5-1.2s1.2.7 1.5 1.2L33.4 16l15.1 2.2c.7.1 1.7.5 1.7 1.4-.2.6-.6 1.1-1 1.5m-5.8.3-12.7-1.9L25 8.1l-5.7 11.5-12.7 1.8 9.2 8.9L13.6 43 25 37l11.3 6-2.2-12.6z";

const StarSVG = (props: { path: string }): React.JSX.Element => (
    <svg
        className="star-rating__star"
        role="img"
        aria-hidden="true"
        xmlns="http://www.w3.org/2000/svg"
        xmlSpace="preserve"
        width="50"
        height="50"
        viewBox="0 0 50 50"
    >
        <path d={props.path} />
    </svg>
);

const getStarPath = (rating: number, starIndex: number): string => {
    // EXACT molecule thresholds: full at >= i + 0.75, half at >= i + 0.25.
    if (rating >= starIndex + 0.75) {
        return STAR_PATH;
    }
    if (rating >= starIndex + 0.25) {
        return STAR_HALF_OUTLINE_PATH;
    }
    return STAR_OUTLINE_PATH;
};

/**
 * Markup-parity port of ``tsicommon_cms/blocks/sub-blocks/star-rating.html``
 * (the variant the legacy tab block included). Structure:
 * ``.star-rating > .star-rating__inner-container > .star-rating__stars``
 * (with the ``ada-screenreader-only`` "Rated {N} out of 5 stars" label and 5
 * star SVGs) + ``.star-rating__number`` ("{N} Reviews"). The molecule's own
 * ``APP_SLUG != 'tempurca'`` gate is enforced here.
 */
const MoleculeStarRating = (
    props: Immutable<{ review: ProductReview }>,
): React.JSX.Element | null => {
    const appSlug = usePageSetting("app_slug") || "";
    if (appSlug === AppSlug.TEMPURCA) {
        return null;
    }
    const rating = props.review.rating ?? 0;
    const number = props.review.num_reviews;
    return (
        <div className="star-rating ">
            <div className="star-rating__inner-container">
                <div className="star-rating__stars">
                    <span
                        className="ada-screenreader-only"
                        aria-label="star rating"
                    >
                        {t`Rated ${rating} out of 5 stars`}
                    </span>
                    {[0, 1, 2, 3, 4].map((starIndex) => (
                        <StarSVG
                            key={starIndex}
                            path={getStarPath(rating, starIndex)}
                        />
                    ))}
                </div>
                {number ? (
                    <div className="star-rating__number">
                        {new Intl.NumberFormat().format(number)} {t`Reviews`}
                    </div>
                ) : null}
            </div>
        </div>
    );
};

const StartingAtCopy = (): React.JSX.Element => <>{t`Starting at`} </>;

/**
 * Hand-render the legacy ``.price`` markup with the BARE global classes the
 * block SCSS targets (``.price``, ``.-actual``, ``.-retail``, ``.-highlight``,
 * ``.-strikethrough``, ``.-underline``). Branch condition is the EXACT legacy
 * expression: ``not price.retail or price.retail == price.cosmetic_excl_tax``.
 * The "Starting at" prefix is gated identically in BOTH branches (on the
 * server-computed ``show_starting_at``).
 */
const ProductTabPrice = (
    props: Immutable<{
        price: ProductPrice;
        showStartingAt: boolean;
        financingUrl: string | null;
    }>,
): React.JSX.Element => {
    const price = props.price;
    const isPlainPrice =
        !price.retail || price.retail === price.cosmetic_excl_tax;
    const startingAt = props.showStartingAt ? <StartingAtCopy /> : null;
    const perMonth = money(price.per_month);
    const termLength = price.per_month_term_length;
    return (
        <div className="price">
            {isPlainPrice ? (
                <span className="-actual">
                    {startingAt}
                    {money(price.cosmetic_excl_tax)}
                </span>
            ) : (
                <>
                    <span className="-retail">
                        {startingAt}
                        <span className="-strikethrough">
                            {money(price.retail)}
                        </span>
                    </span>
                    <span className="-highlight">
                        {money(price.cosmetic_excl_tax)}
                    </span>
                </>
            )}
            {price.per_month ? (
                <a href={`${props.financingUrl ?? ""}#terms_and_conditions`}>
                    {/* Money/number values are the only interpolated
                        placeholders; the surrounding prose ("or", "/mo.",
                        "for", "months") lives in a single ``t`` template so
                        translators can reorder the whole sentence (legacy
                        single ``blocktrans`` parity). The ``-underline`` spans
                        and ``<sup>`` footnote markers are markup tags resolved
                        by ``richT`` so the rendered DOM matches legacy. */}
                    {richT(
                        t`or <permonth>${perMonth}/mo.</permonth><sup>2</sup> for <term>${termLength} months</term><sup>1</sup>`,
                        {
                            permonth: (children) => (
                                <span className="-underline">{children}</span>
                            ),
                            term: (children) => (
                                <span className="-underline">{children}</span>
                            ),
                        },
                    )}
                </a>
            ) : null}
        </div>
    );
};

const ProductTabPanel = (
    props: Immutable<{
        tab: ProductTab;
        active: boolean;
        financingUrl: string | null;
    }>,
): React.JSX.Element => {
    const tab = props.tab;
    const info = tab.product_info;
    return (
        <div
            id={tab.panel_id}
            role="tabpanel"
            tabIndex={0}
            aria-labelledby={tab.tab_id}
            hidden={!props.active}
        >
            <RichTextBlock
                value={tab.tab_content ?? ""}
                className="rich-text"
            />

            {info?.review ? (
                <a
                    title={t`Read reviews for ${info.title}`}
                    href={`${info.url}#reviews`}
                >
                    <MoleculeStarRating review={info.review} />
                </a>
            ) : null}

            {info?.price ? (
                <ProductTabPrice
                    price={info.price}
                    showStartingAt={info.show_starting_at}
                    financingUrl={props.financingUrl}
                />
            ) : null}

            <RichTextBlock
                value={tab.testimonial ?? ""}
                className="rich-text"
            />
        </div>
    );
};

export const FeaturedProductsTabBlock = (
    props: Immutable<{ value: FeaturedProductsTabBlockValue }>,
): React.JSX.Element => {
    const v = props.value;
    const tabs = v.product_tabs ?? [];
    const sectionRef = useRef<HTMLElement | null>(null);
    const firedRef = useRef(false);
    const { activeIndex, setActiveIndex, getTabProps, tabListRef } =
        useRovingTabList({
            count: tabs.length,
            scrollActiveIntoView: true,
        });

    // Deep-link support: on mount, open the tab named by the URL hash (matched
    // against either the tab button or the panel id). Runs post-hydration so
    // the SSR render stays on tab 0 and no hydration mismatch is introduced.
    useEffect(() => {
        const hash = getWindow()?.location.hash;
        if (!hash) {
            return;
        }
        const id = hash.slice(1);
        const idx = tabs.findIndex(
            (tab) => tab.tab_id === id || tab.panel_id === id,
        );
        if (idx >= 0) {
            setActiveIndex(idx);
        }
        // Mount-only: the URL hash is read once, as the legacy on-load bind did.
    }, []);

    // Fire the ``view_item_list`` impression exactly once on mount from the
    // PRE-BUILT server event (same ``_build_item_list_event`` builder as the
    // legacy ``{% track_view_item_list %}`` tag; null under DNT). The
    // visibility gate mirrors the legacy ``push_to_datalayer.html``
    // ``_isVisible`` walk.
    useEffect(() => {
        if (firedRef.current) {
            return;
        }
        firedRef.current = true;
        if (!isElementVisible(sectionRef.current)) {
            return;
        }
        pushPrebuiltGA4Event(v.view_item_list_event);
        // Fire-on-mount only: the block value is static per page render.
    }, []);

    return (
        <section
            ref={sectionRef}
            className={concatClassNames([
                "featured-products-tab-block",
                getGlobalSpacingClasses(v.spacing),
            ])}
        >
            <nav
                ref={tabListRef}
                role="tablist"
                aria-label={v.aria_label || "Featured Products"}
            >
                <RichTextBlock
                    value={v.header_content ?? ""}
                    className="rich-text"
                />

                {tabs.map((tab, i) => (
                    <button
                        key={i}
                        id={tab.tab_id}
                        type="button"
                        role="tab"
                        aria-controls={tab.panel_id}
                        {...getTabProps(i)}
                    >
                        <RichTextBlock
                            value={tab.tab_label ?? ""}
                            className="rich-text"
                        />
                    </button>
                ))}
            </nav>

            {tabs.map((tab, i) => (
                <ProductTabPanel
                    key={i}
                    tab={tab}
                    active={i === activeIndex}
                    financingUrl={v.financing_url}
                />
            ))}
        </section>
    );
};
